import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Syringe, Bell } from "lucide-react";
import {
  vaccinationApi,
  vaccinationQueryKeys,
  type VaxProtocol,
} from "../../shared/api/vaccination";
import { medicineQueryKeys, medicinesApi } from "../../shared/api/medicines";
import { inventoryApi, inventoryQueryKeys } from "../../shared/api/inventory";
import { apiError } from "../../shared/api/client";
import { formatVnd } from "../../shared/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

/**
 * Tiêm chủng · Danh mục & Phác đồ (P0A). Manage vaccine protocols (each linked to a VACCINE-class
 * medicine), their dose timelines (data-driven intervals/age gates), and the screening-checklist
 * templates. The vaccine products themselves live in the shared medicine catalog.
 */
export function VaccinationCatalogPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<VaxProtocol | null>(null);
  const [protoOpen, setProtoOpen] = useState(false);

  const protocols = useQuery({
    queryKey: vaccinationQueryKeys.protocols(),
    queryFn: () => vaccinationApi.listProtocols(),
  });

  const vaccines = useQuery({
    queryKey: medicineQueryKeys.list({ classification: "VACCINE" }, 0),
    queryFn: () =>
      medicinesApi.list({ classification: "VACCINE" }, { page: 0, size: 200, sort: "name" }),
  });
  const vaccineName = useMemo(() => {
    const m = new Map<string, string>();
    vaccines.data?.content.forEach((v) => m.set(v.id, v.name));
    return m;
  }, [vaccines.data]);
  const vaccinePrice = useMemo(() => {
    const m = new Map<string, number>();
    vaccines.data?.content.forEach((v) => m.set(v.id, v.sellPriceVnd));
    return m;
  }, [vaccines.data]);

  const stock = useQuery({
    queryKey: inventoryQueryKeys.stock(),
    queryFn: () => inventoryApi.stock(),
    staleTime: 60 * 1000,
  });
  const stockQty = useMemo(() => {
    const m = new Map<string, number>();
    (stock.data ?? []).forEach((s) =>
      m.set(s.medicineId, (m.get(s.medicineId) ?? 0) + (s.quantityOnHand ?? 0))
    );
    return m;
  }, [stock.data]);

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-foreground">Danh mục vắc xin &amp; Phác đồ</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Mỗi phác đồ gắn với một vắc xin (thuốc phân loại “Vắc xin”) và mô tả timeline các mũi.
            Khoảng cách/độ tuổi là dữ liệu — không cố định trong mã.
          </p>
        </div>
        <div className="shrink-0">
          <Button type="button" onClick={() => setProtoOpen(true)}>
            <Plus />
            Thêm phác đồ
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
        <Card>
          <CardContent className="p-2">
            {protocols.isLoading && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">Đang tải…</div>
            )}
            {!protocols.isLoading && (protocols.data?.length ?? 0) === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Chưa có phác đồ. Bấm “Thêm phác đồ”.
              </div>
            )}
            <div className="flex flex-col gap-1">
              {protocols.data?.map((p) => {
                const active = selected?.id === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors ${
                      active
                        ? "border-primary bg-primary/10"
                        : "border-transparent hover:bg-muted"
                    }`}
                    onClick={() => setSelected(p)}
                  >
                    <Syringe className="size-5 shrink-0 text-primary" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground">
                        {p.name}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {p.disease ?? "—"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <DosePanel
          protocol={selected}
          priceVnd={selected ? vaccinePrice.get(selected.vaccineMedicineId) ?? null : null}
          stockQty={selected ? stockQty.get(selected.vaccineMedicineId) ?? 0 : 0}
          vaccineLabel={selected ? vaccineName.get(selected.vaccineMedicineId) ?? null : null}
        />
      </div>

      <TemplatesSection />

      {protoOpen && (
        <CreateProtocolModal
          vaccines={vaccines.data?.content ?? []}
          onClose={() => setProtoOpen(false)}
          onSaved={() => {
            setProtoOpen(false);
            qc.invalidateQueries({ queryKey: vaccinationQueryKeys.protocols() });
          }}
        />
      )}
    </div>
  );
}

function DosePanel({
  protocol,
  priceVnd,
  stockQty,
  vaccineLabel,
}: {
  protocol: VaxProtocol | null;
  priceVnd: number | null;
  stockQty: number;
  vaccineLabel: string | null;
}) {
  const qc = useQueryClient();
  const [doseNo, setDoseNo] = useState(1);
  const [label, setLabel] = useState("");
  const [anchor, setAnchor] = useState<"SERIES_START" | "PREV_DOSE" | "AGE">("PREV_DOSE");
  const [minInt, setMinInt] = useState<string>("");
  const [recInt, setRecInt] = useState<string>("");
  const [minAge, setMinAge] = useState<string>("");
  const [booster, setBooster] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const doses = useQuery({
    queryKey: vaccinationQueryKeys.doses(protocol?.id ?? "none"),
    queryFn: () => vaccinationApi.listDoses(protocol!.id),
    enabled: !!protocol,
  });

  const add = useMutation({
    mutationFn: () =>
      vaccinationApi.addDose(protocol!.id, {
        doseNo,
        label: label || null,
        anchorBasis: anchor,
        minIntervalDays: minInt ? Number(minInt) : null,
        recommendedIntervalDays: recInt ? Number(recInt) : null,
        minAgeDays: minAge ? Number(minAge) : null,
        maxAgeDays: null,
        booster,
      }),
    onSuccess: () => {
      setErr(null);
      setLabel("");
      setMinInt("");
      setRecInt("");
      setMinAge("");
      setDoseNo(doseNo + 1);
      qc.invalidateQueries({ queryKey: vaccinationQueryKeys.doses(protocol!.id) });
    },
    onError: (e) => setErr(apiError(e).detail),
  });

  if (!protocol) {
    return (
      <Card>
        <CardContent className="flex min-h-40 items-center justify-center p-4 text-sm text-muted-foreground">
          Chọn một phác đồ để xem &amp; thêm mũi.
        </CardContent>
      </Card>
    );
  }

  const doseList = doses.data ?? [];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-foreground">{protocol.name}</h3>
            <div className="text-sm text-muted-foreground">{protocol.disease ?? "—"}</div>
          </div>
          <Badge variant="muted">v{protocol.versionNo}</Badge>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="font-semibold text-foreground">{vaccineLabel ?? "—"}</span>
          <span className="text-muted-foreground">
            {priceVnd != null ? formatVnd(priceVnd) : "—"}
          </span>
          <span
            className={
              stockQty > 0
                ? "font-medium text-emerald-600"
                : "font-medium text-destructive"
            }
          >
            {stockQty > 0 ? `Còn ${stockQty} liều` : "Hết hàng"}
          </span>
        </div>

        {/* Dose timeline stepper */}
        {doseList.length === 0 ? (
          <div className="mt-4 rounded-md border border-dashed bg-muted/40 px-3 py-6 text-center text-sm text-muted-foreground">
            Chưa có mũi. Bấm “Thêm mũi” bên dưới.
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap items-start gap-2">
            {doseList.map((d, i) => (
              <div className="flex items-start gap-2" key={d.id}>
                {i > 0 && (
                  <div className="flex flex-col items-center justify-center gap-1 self-center pt-1">
                    <span className="h-px w-8 bg-border" />
                    <span className="text-[11px] font-medium text-muted-foreground">
                      {intervalLabel(d)}
                    </span>
                  </div>
                )}
                <div
                  className={`flex w-24 flex-col items-center gap-1 rounded-md border p-2 text-center ${
                    d.booster ? "border-amber-300 bg-amber-50" : "border-input bg-background"
                  }`}
                >
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {d.booster ? (
                      <Bell className="size-4" aria-hidden="true" />
                    ) : (
                      <span className="text-base leading-none">{circled(d.doseNo)}</span>
                    )}
                  </span>
                  <span className="text-xs font-semibold text-foreground">
                    {d.label ?? `Mũi ${d.doseNo}`}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{anchorLabel(d)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() => setAddOpen((v) => !v)}
        >
          {addOpen ? (
            "Đóng"
          ) : (
            <>
              <Plus />
              Thêm mũi
            </>
          )}
        </Button>

        {addOpen && (
          <div className="mt-3 border-t pt-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="dose-no">Mũi số</Label>
                <Input
                  id="dose-no"
                  type="number"
                  min={1}
                  value={doseNo}
                  onChange={(e) => setDoseNo(Number(e.target.value) || 1)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dose-label">Nhãn</Label>
                <Input
                  id="dose-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Mũi 1"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dose-anchor">Mốc tính</Label>
                <Select
                  value={anchor}
                  onValueChange={(v) => setAnchor(v as typeof anchor)}
                >
                  <SelectTrigger id="dose-anchor">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SERIES_START">Từ mũi đầu</SelectItem>
                    <SelectItem value="PREV_DOSE">Từ mũi trước</SelectItem>
                    <SelectItem value="AGE">Theo tuổi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="dose-min-int">Tối thiểu (ngày)</Label>
                <Input
                  id="dose-min-int"
                  type="number"
                  value={minInt}
                  onChange={(e) => setMinInt(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dose-rec-int">Khuyến nghị (ngày)</Label>
                <Input
                  id="dose-rec-int"
                  type="number"
                  value={recInt}
                  onChange={(e) => setRecInt(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dose-min-age">Tuổi tối thiểu (ngày)</Label>
                <Input
                  id="dose-min-age"
                  type="number"
                  value={minAge}
                  onChange={(e) => setMinAge(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Label className="flex items-center gap-2 pb-2 font-normal">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={booster}
                    onChange={(e) => setBooster(e.target.checked)}
                  />
                  Mũi nhắc
                </Label>
              </div>
            </div>
            {err && <p className="mt-3 text-sm text-destructive">{err}</p>}
            <Button
              type="button"
              variant="secondary"
              className="mt-3"
              disabled={add.isPending}
              onClick={() => add.mutate()}
            >
              <Plus />
              Thêm mũi
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** ① ② ③ … circled dose number; falls back to the plain number past ⑩. */
function circled(n: number): string {
  const marks = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫";
  return n >= 1 && n <= marks.length ? marks[n - 1] : String(n);
}

/** Interval to the previous dose, humanised (365→"1 năm", 30→"1 tháng"). */
function intervalLabel(d: { recommendedIntervalDays?: number | null; minIntervalDays?: number | null }): string {
  const days = d.recommendedIntervalDays ?? d.minIntervalDays;
  if (days == null) return "→";
  if (days % 365 === 0) return `+${days / 365} năm`;
  if (days % 30 === 0) return `+${days / 30} tháng`;
  return `+${days} ngày`;
}

function anchorLabel(d: {
  anchorBasis: string;
  minAgeDays?: number | null;
  booster: boolean;
}): string {
  if (d.anchorBasis === "SERIES_START") return "bắt đầu";
  if (d.anchorBasis === "AGE" && d.minAgeDays != null) return `${d.minAgeDays} ngày tuổi`;
  return d.booster ? "nhắc lại" : "";
}

function TemplatesSection() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const templates = useQuery({
    queryKey: vaccinationQueryKeys.templates(),
    queryFn: () => vaccinationApi.listTemplates(),
  });
  return (
    <section className="mt-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-lg font-bold text-foreground">Bảng kiểm sàng lọc (template)</h2>
          <p className="text-sm text-muted-foreground">
            Data-driven, có phiên bản. VD: QĐ 1575 Phụ lục I.
          </p>
        </div>
        <div className="shrink-0">
          <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
            <Plus />
            Thêm template
          </Button>
        </div>
      </div>
      {(templates.data?.length ?? 0) === 0 ? (
        <Card className="mt-3">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Chưa có template.
          </CardContent>
        </Card>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {templates.data?.map((t) => (
            <div key={t.id} className="rounded-lg border bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-semibold tabular-nums text-foreground">
                  {t.code}
                </span>
                <Badge variant="muted">v{t.versionNo}</Badge>
              </div>
              <div className="mt-1 font-medium text-foreground">{t.title}</div>
              <div className="text-sm text-muted-foreground">
                Nguồn: {t.sourceRef ?? "—"}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <CreateTemplateModal
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            qc.invalidateQueries({ queryKey: vaccinationQueryKeys.templates() });
          }}
        />
      )}
    </section>
  );
}

function CreateProtocolModal({
  vaccines,
  onClose,
  onSaved,
}: {
  vaccines: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [disease, setDisease] = useState("");
  const [vaccineId, setVaccineId] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      vaccinationApi.createProtocol({
        code,
        name,
        disease: disease || null,
        vaccineMedicineId: vaccineId,
        sourceRef: null,
      }),
    onSuccess: onSaved,
    onError: (e) => setErr(apiError(e).detail),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Thêm phác đồ</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {vaccines.length === 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Chưa có vắc xin trong danh mục thuốc. Vào “Thuốc + Catalog” tạo một mục phân loại “Vắc
              xin” trước.
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="proto-code">Mã phác đồ *</Label>
            <Input
              id="proto-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="ABC-STD"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="proto-name">Tên *</Label>
            <Input id="proto-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="proto-disease">Bệnh</Label>
            <Input
              id="proto-disease"
              value={disease}
              onChange={(e) => setDisease(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="proto-vaccine">Vắc xin *</Label>
            <Select value={vaccineId} onValueChange={setVaccineId}>
              <SelectTrigger id="proto-vaccine">
                <SelectValue placeholder="— Chọn vắc xin —" />
              </SelectTrigger>
              <SelectContent>
                {vaccines.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Hủy
          </Button>
          <Button
            type="button"
            disabled={save.isPending || !code || !name || !vaccineId}
            onClick={() => save.mutate()}
          >
            Tạo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateTemplateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState("QD1575-PL1");
  const [versionNo, setVersionNo] = useState(1);
  const [title, setTitle] = useState("Bảng kiểm trước tiêm ≥1 tháng");
  const [sourceRef, setSourceRef] = useState("Phụ lục I, QĐ 1575/QĐ-BYT");
  const [itemsJson, setItemsJson] = useState(
    '[{"no":1,"text":"Phản vệ độ III sau mũi trước","actor":"PATIENT_DECLARE","onAbnormal":"CONTRAINDICATE"},{"no":8,"text":"Nghe tim bất thường","actor":"CLINICIAN","onAbnormal":"DEFER"}]',
  );
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      vaccinationApi.createTemplate({
        code,
        versionNo,
        title,
        sourceRef,
        itemsJson,
      }),
    onSuccess: onSaved,
    onError: (e) => setErr(apiError(e).detail),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Thêm template sàng lọc</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-code">Mã *</Label>
              <Input id="tpl-code" value={code} onChange={(e) => setCode(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-version">Phiên bản</Label>
              <Input
                id="tpl-version"
                type="number"
                min={1}
                value={versionNo}
                onChange={(e) => setVersionNo(Number(e.target.value) || 1)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tpl-title">Tiêu đề *</Label>
            <Input id="tpl-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tpl-source">Nguồn</Label>
            <Input
              id="tpl-source"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
            />
          </div>
          <Separator />
          <div className="space-y-1.5">
            <Label htmlFor="tpl-items">Items (JSON)</Label>
            <Textarea
              id="tpl-items"
              rows={5}
              value={itemsJson}
              onChange={(e) => setItemsJson(e.target.value)}
              className="font-mono text-xs"
            />
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Hủy
          </Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
            Tạo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
